/**
 * Payment-rail attribution for a /mulehunter/screen hit.
 *
 * The backend (services/mulehunter/service.py `_rail_attribution`, vocabulary in
 * rails_registry.py) reports issuer facts beside a hit: the PSP behind a VPA,
 * the bank/branch behind an IFSC, the rail and decoded date of a UTR, the
 * country of a phone. Nothing here is a score; the chip states the fact and
 * the tooltip carries the raw evidence fields so an analyst can check it.
 */
import { clsx } from 'clsx';
import type {
    MulehunterIfscFormatRail,
    MulehunterIfscMasterRail,
    MulehunterPhoneRail,
    MulehunterRailAttribution,
    MulehunterUtrRail,
    MulehunterVpaRail,
} from '../../services/api';

type Tone = 'normal' | 'warn' | 'muted';

const TONE: Record<Tone, string> = {
    normal: 'bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/20',
    warn: 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30',
    muted: 'bg-muted text-muted-foreground border-border',
};

interface ChipModel {
    label: string;
    tone: Tone;
    tags: string[];
}

/** "key: value" lines over every non-empty field, for the title tooltip. */
export function railTooltip(rail: object): string {
    return Object.entries(rail)
        .filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0))
        .map(([k, v]) => {
            if (Array.isArray(v)) {
                return `${k}: ${v.map(e => (typeof e === 'object' ? Object.values(e as object).filter(Boolean).join(' / ') : String(e))).join('; ')}`;
            }
            return `${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`;
        })
        .join('\n');
}

function vpaModel(r: MulehunterVpaRail): ChipModel {
    const tags: string[] = [];
    if (r.merchant_aggregator) tags.push('merchant');
    if (r.sebi_valid) tags.push('SEBI @valid');
    if (!r.valid_format) {
        return { label: `Not a UPI handle${r.handle ? ` · @${r.handle}` : ''}`, tone: 'muted', tags };
    }
    const issuer = r.psp_bank || r.psp_app;
    if (!issuer) {
        return { label: `@${r.handle} · not in registry`, tone: 'muted', tags };
    }
    const verified = r.psp_confidence === 'verified';
    return {
        label: `${issuer} · @${r.handle} (${verified ? 'verified' : 'unverified'})`,
        tone: verified ? 'normal' : 'warn',
        tags,
    };
}

function utrModel(r: MulehunterUtrRail): ChipModel {
    const rail = r.rail === 'unknown' ? 'Unknown rail' : r.rail.toUpperCase();
    const parts = [rail];
    if (r.txn_date) parts.push(r.txn_date);
    else if (r.bank_code) parts.push(r.bank_code);
    const tone: Tone = r.rail === 'unknown' ? 'muted'
        : r.date_confidence === 'decade_fallback' ? 'warn' : 'normal';
    const tags = r.date_confidence === 'decade_fallback' ? ['date doubtful'] : [];
    return { label: parts.join(' · '), tone, tags };
}

function ifscModel(r: MulehunterIfscMasterRail | MulehunterIfscFormatRail): ChipModel {
    if (r.source === 'ifsc_master') {
        const place = [r.branch, r.city].filter(Boolean).join(', ');
        return { label: [r.bank || r.bank_code, place].filter(Boolean).join(' · '), tone: 'normal', tags: [] };
    }
    if (!r.valid_format) return { label: 'Invalid IFSC format', tone: 'muted', tags: [] };
    return { label: `${r.bank_code} · branch ${r.branch_code} (format only)`, tone: 'muted', tags: [] };
}

function phoneModel(r: MulehunterPhoneRail): ChipModel {
    const conf = r.confidence ?? 'unknown';
    if (!r.region) return { label: 'Region unknown', tone: 'muted', tags: [] };
    const tone: Tone = conf === 'explicit' || conf === 'context' ? 'normal' : 'muted';
    return { label: `${r.region} · ${conf.replace('_', ' ')}`, tone, tags: [] };
}

/** Pure: the chip text/tone for a hit, or null when there is nothing to show. */
export function railChipModel(idType: string, rail: MulehunterRailAttribution | null | undefined): ChipModel | null {
    if (!rail || Object.keys(rail).length === 0) return null;
    switch (idType) {
        case 'upi': return vpaModel(rail as MulehunterVpaRail);
        case 'utr': return utrModel(rail as MulehunterUtrRail);
        case 'ifsc': return ifscModel(rail as MulehunterIfscMasterRail | MulehunterIfscFormatRail);
        case 'phone': return phoneModel(rail as MulehunterPhoneRail);
        default: return null;
    }
}

export function RailChip({ idType, rail }: { idType: string; rail: MulehunterRailAttribution | null | undefined }) {
    const model = railChipModel(idType, rail);
    if (!model || !rail) return <span className="text-muted-foreground text-xs">—</span>;
    return (
        <span className="inline-flex flex-wrap items-center gap-1" title={railTooltip(rail)} data-testid="rail-chip">
            <span className={clsx('inline-flex items-center px-2 py-0.5 text-[11px] rounded-md border whitespace-nowrap', TONE[model.tone])}>
                {model.label}
            </span>
            {model.tags.map(t => (
                <span key={t} className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full border bg-violet-500/10 text-violet-700 dark:text-violet-300 border-violet-500/20">
                    {t}
                </span>
            ))}
        </span>
    );
}

/** Matched VPAs by the PSP bank that carries them. Renders nothing when empty. */
export function PspBankSummary({ byPspBank }: { byPspBank: Record<string, number> | null | undefined }) {
    const rows = Object.entries(byPspBank || {}).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    if (rows.length === 0) return null;
    const total = rows.reduce((s, [, n]) => s + n, 0);
    return (
        <div className="rounded-md border border-border bg-card px-3 py-2" data-testid="psp-bank-summary">
            <div className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">
                Matched VPAs by PSP bank ({total})
            </div>
            <div className="flex flex-wrap gap-1.5">
                {rows.map(([bank, n]) => (
                    <span
                        key={bank}
                        className={clsx(
                            'inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-md border',
                            bank === 'unknown' ? TONE.muted : 'border-border bg-background text-foreground',
                        )}
                        title={bank === 'unknown' ? 'Handle not in the PSP registry, bank not stated for the handle, or not a valid VPA' : undefined}
                    >
                        {bank === 'unknown' ? 'Unattributed' : bank}
                        <span className="tabular-nums font-semibold">{n}</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
